// ============================================================================
// TOURNAMENT CHALLENGE RUNNER
// ============================================================================
// Plays one challenge gauntlet: decision → instant feedback + lesson → next.
// Finishes with a review screen (every decision, your pick, the lesson - a
// hand-history-style recap) and a server-validated PSS Coin claim.
// ============================================================================
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Container, Heading, Text, VStack, HStack, Badge, Progress,
} from '@chakra-ui/react';
import { ArrowLeft, CheckCircle, XCircle, MinusCircle, Coins, RotateCcw } from 'lucide-react';
import { POKER_COLORS } from '../theme/designSystem';
import PokerCard from '../components/ui/PokerCard';
import PokerButton from '../components/ui/PokerButton';
import gameApiService from '../services/gameApiService';
import geoService from '../services/geoService';
import {
  getChallengeById, scoreRun, StepCard, StepOption, TIER_META, getSkillLabel,
} from '../services/training/tournamentChallenges';

const suitGlyph = (s: string) =>
  s === 'hearts' ? '♥' : s === 'diamonds' ? '♦' : s === 'clubs' ? '♣' : '♠';
const suitColor = (s: string) => (s === 'hearts' || s === 'diamonds' ? '#e53e3e' : '#f8fafc');

const HoleCard: React.FC<{ card: StepCard }> = ({ card }) => (
  <Box w="56px" h="78px" borderRadius="8px" bg="white" border="1px solid #cbd5e0"
    display="flex" flexDirection="column" alignItems="center" justifyContent="center"
    boxShadow="0 4px 12px rgba(0,0,0,0.4)">
    <Text fontSize="24px" fontWeight="800" lineHeight="1"
      color={card.suit === 'hearts' || card.suit === 'diamonds' ? '#e53e3e' : '#1a202c'}>
      {card.rank}
    </Text>
    <Text fontSize="20px" lineHeight="1"
      color={card.suit === 'hearts' || card.suit === 'diamonds' ? '#e53e3e' : '#1a202c'}>
      {suitGlyph(card.suit)}
    </Text>
  </Box>
);

const evalMeta = {
  best: { icon: CheckCircle, color: '#4ade80', label: 'Best play' },
  ok:   { icon: MinusCircle, color: '#fbbf24', label: 'Playable' },
  bad:  { icon: XCircle,     color: '#f87171', label: 'Costly' },
} as const;

const TournamentChallengePage: React.FC = () => {
  const navigate = useNavigate();
  const { challengeId } = useParams<{ challengeId: string }>();
  const challenge = useMemo(() => (challengeId ? getChallengeById(challengeId) : undefined), [challengeId]);

  const [stepIdx, setStepIdx] = useState(0);
  const [picked, setPicked] = useState<StepOption | null>(null);
  const [answers, setAnswers] = useState<Array<{ stepId: string; action: string; evaluation: StepOption['evaluation'] }>>([]);
  const [finished, setFinished] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState<{ ok: boolean; message: string } | null>(null);

  if (!challenge || challenge.comingSoon) {
    return (
      <Box minH="100vh" bg="gray.900" pt={10}>
        <Container maxW="2xl" textAlign="center">
          <Text color="gray.400">Challenge not available.</Text>
          <PokerButton size="sm" onClick={() => navigate('/training/tournament')} style={{ marginTop: 16 }}>
            Back to Tournament Training
          </PokerButton>
        </Container>
      </Box>
    );
  }

  const step = challenge.steps[stepIdx];
  const tier = TIER_META[challenge.tier];

  const choose = (opt: StepOption) => {
    if (picked) return;
    setPicked(opt);
    setAnswers(prev => [...prev, { stepId: step.id, action: opt.action, evaluation: opt.evaluation }]);
  };

  const next = () => {
    setPicked(null);
    if (stepIdx + 1 >= challenge.steps.length) setFinished(true);
    else setStepIdx(stepIdx + 1);
  };

  const restart = () => {
    setStepIdx(0);
    setPicked(null);
    setAnswers([]);
    setFinished(false);
    setClaimResult(null);
  };

  const { points, passed } = scoreRun(challenge, answers);

  const claim = async () => {
    setClaiming(true);
    // Same geo pattern as every training claim: send the detected state so
    // the server credits PSS Coins (legal states) or PlayCoins (restricted).
    const stateCode = geoService.getCachedResult()?.region;
    const r = await gameApiService.claimTournamentChallenge(challenge.id, answers, stateCode);
    setClaiming(false);
    if (r.success) {
      setClaimResult({
        ok: true,
        message: r.alreadyClaimed
          ? 'Already claimed - rewards pay once per challenge.'
          : r.geoRestricted
            ? `+${(r.playCoins ?? Math.round(challenge.rewardPkCash * 100)).toLocaleString()} PlayCoins credited (PSS Coins unavailable in your state).`
            : `+$${(r.rewardPkCash ?? challenge.rewardPkCash).toFixed(2)} PSS Coins credited (3x playthrough).`,
      });
    } else {
      setClaimResult({ ok: false, message: r.error || 'Claim failed' });
    }
  };

  // ── REVIEW SCREEN ────────────────────────────────────────────────────────
  if (finished) {
    return (
      <Box minH="100vh" bg="gray.900" pb={16}>
        <Container maxW="3xl" pt={6}>
          <Heading size="md" color="white" mb={1}>{challenge.title} - Review</Heading>
          <Text color="gray.400" fontSize="sm" mb={4}>
            Score: <Text as="span" color={passed ? 'green.300' : 'red.300'} fontWeight="bold">
              {points} / {challenge.steps.length}
            </Text>{' '}(pass: {challenge.passThreshold}+)
          </Text>

          <PokerCard>
            <VStack align="stretch" spacing={0} p={2}>
              <HStack justify="center" py={3}>
                {passed ? (
                  <Badge colorScheme="green" fontSize="md" px={4} py={2} borderRadius="lg">
                    CHALLENGE PASSED
                  </Badge>
                ) : (
                  <Badge colorScheme="red" fontSize="md" px={4} py={2} borderRadius="lg">
                    NOT PASSED - REVIEW & RETRY
                  </Badge>
                )}
              </HStack>

              {passed && !claimResult && (
                <PokerButton onClick={claim} isLoading={claiming} style={{ margin: '0 auto 12px' }}>
                  <Coins size={14} style={{ marginRight: 6 }} />
                  Claim ${challenge.rewardPkCash.toFixed(2)} PSS Coins
                </PokerButton>
              )}
              {claimResult && (
                <Text textAlign="center" pb={3} fontSize="sm"
                  color={claimResult.ok ? 'green.300' : 'red.300'}>
                  {claimResult.message}
                </Text>
              )}
            </VStack>
          </PokerCard>

          {/* Decision-by-decision recap (hand-history style) */}
          <VStack align="stretch" spacing={3} mt={5}>
            {challenge.steps.map((s, i) => {
              const a = answers[i];
              const em = a ? evalMeta[a.evaluation] : null;
              const Icon = em?.icon;
              return (
                <PokerCard key={s.id}>
                  <VStack align="stretch" spacing={2} p={1}>
                    <HStack justify="space-between">
                      <HStack>
                        <Badge fontSize="9px" colorScheme="gray">{s.position}</Badge>
                        <Text color="gray.400" fontSize="xs">{s.stage} · {s.heroBB}bb</Text>
                      </HStack>
                      {em && Icon && (
                        <HStack>
                          <Icon size={14} color={em.color} />
                          <Text fontSize="xs" color={em.color} fontWeight="bold">{em.label}</Text>
                        </HStack>
                      )}
                    </HStack>
                    <HStack>
                      <Text color="white" fontSize="sm" fontWeight="600">
                        {s.hand[0].rank}{suitGlyph(s.hand[0].suit)} {s.hand[1].rank}{suitGlyph(s.hand[1].suit)}
                      </Text>
                      <Text color="gray.500" fontSize="sm">- you chose: {a?.action || '-'}</Text>
                    </HStack>
                    <Text color="gray.300" fontSize="xs">
                      <Text as="span" color={POKER_COLORS.cyan} fontWeight="bold">Lesson: </Text>
                      {s.keyLesson}
                    </Text>
                  </VStack>
                </PokerCard>
              );
            })}
          </VStack>

          <HStack mt={6} justify="center" spacing={4}>
            <PokerButton variant="secondary" onClick={restart}>
              <RotateCcw size={14} style={{ marginRight: 6 }} /> Retry
            </PokerButton>
            <PokerButton onClick={() => navigate('/training/tournament')}>
              All Challenges
            </PokerButton>
          </HStack>
        </Container>
      </Box>
    );
  }

  // ── DECISION SCREEN ──────────────────────────────────────────────────────
  return (
    <Box minH="100vh" bg="gray.900" pb={16}>
      <Container maxW="2xl" pt={6}>
        <HStack mb={3} justify="space-between">
          <PokerButton size="sm" variant="secondary" onClick={() => navigate('/training/tournament')}>
            <ArrowLeft size={14} style={{ marginRight: 4 }} /> Exit
          </PokerButton>
          <HStack>
            <Text
              fontSize="xs" color={POKER_COLORS.cyan} cursor="pointer" textDecoration="underline"
              onClick={() => window.open('/training/glossary?category=tournament', '_blank')}
              title="Poker terms explained in plain English (opens in a new tab)"
            >
              Glossary ↗
            </Text>
            <Badge bg={`${tier.color}33`} color={tier.color} px={2} borderRadius="md">
              {tier.label}
            </Badge>
          </HStack>
        </HStack>

        <HStack mb={0.5}>
          <Heading size="md" color="white">{challenge.title}</Heading>
          {challenge.proSeries && (
            <Badge bg="linear-gradient(135deg, #b91c1c, #f59e0b)" color="white" fontSize="9px"
              px={2} borderRadius="full" letterSpacing="0.5px">
              PRO SERIES
            </Badge>
          )}
        </HStack>
        {challenge.proSeries && (
          <Text color="#f59e0b" fontSize="xs" fontWeight="semibold" mb={0.5}>
            Real spots from a verified top-ranked tournament pro - the decisions that made real money.
          </Text>
        )}
        <Text color={POKER_COLORS.cyan} fontSize="xs" fontWeight="semibold" mb={2}>
          Improves: {getSkillLabel(challenge.concept)}
        </Text>
        <Progress value={((stepIdx) / challenge.steps.length) * 100} size="xs"
          colorScheme="cyan" borderRadius="full" mb={1} />
        <Text color="gray.500" fontSize="xs" mb={4}>
          Decision {stepIdx + 1} of {challenge.steps.length}
        </Text>

        <PokerCard>
          <VStack align="stretch" spacing={4} p={2}>
            <HStack justify="space-between">
              <Text color="gray.400" fontSize="xs">{step.stage}</Text>
              <HStack>
                <Badge colorScheme="cyan" fontSize="10px">{step.position}</Badge>
                <Badge colorScheme="purple" fontSize="10px">{step.heroBB}bb</Badge>
              </HStack>
            </HStack>

            <HStack justify="center" spacing={3} py={2}>
              <HoleCard card={step.hand[0]} />
              <HoleCard card={step.hand[1]} />
            </HStack>

            <Box bg="whiteAlpha.100" borderRadius="md" p={3}>
              <Text color="gray.200" fontSize="sm">{step.situation}</Text>
              <Text color={POKER_COLORS.cyan} fontSize="sm" fontWeight="600" mt={1}>
                {step.actionTo}
              </Text>
            </Box>

            <VStack align="stretch" spacing={2}>
              {step.options.map((opt, i) => {
                const answered = !!picked;
                const isPick = picked?.action === opt.action;
                const em = evalMeta[opt.evaluation];
                return (
                  <Box
                    key={i}
                    onClick={() => choose(opt)}
                    cursor={answered ? 'default' : 'pointer'}
                    bg={answered
                      ? (opt.evaluation === 'best' ? 'rgba(74,222,128,0.12)'
                        : isPick ? 'rgba(248,113,113,0.10)' : 'whiteAlpha.50')
                      : 'whiteAlpha.100'}
                    border="1px solid"
                    borderColor={answered
                      ? (opt.evaluation === 'best' ? 'green.500'
                        : isPick ? (opt.evaluation === 'ok' ? 'yellow.500' : 'red.500') : 'whiteAlpha.200')
                      : 'whiteAlpha.300'}
                    borderRadius="md" px={4} py={3}
                    _hover={answered ? {} : { bg: 'whiteAlpha.200' }}
                    transition="all 0.15s"
                  >
                    <HStack justify="space-between">
                      <Text color="white" fontSize="sm" fontWeight="600">{opt.action}</Text>
                      {answered && (isPick || opt.evaluation === 'best') && (
                        <Text fontSize="xs" color={em.color} fontWeight="bold">{em.label}</Text>
                      )}
                    </HStack>
                    {answered && (isPick || opt.evaluation === 'best') && (
                      <Text color="gray.300" fontSize="xs" mt={1}>{opt.explanation}</Text>
                    )}
                  </Box>
                );
              })}
            </VStack>

            {picked && (
              <>
                <Box bg={`${POKER_COLORS.cyan}11`} border="1px solid" borderColor={`${POKER_COLORS.cyan}44`}
                  borderRadius="md" p={3}>
                  <Text fontSize="xs" color={POKER_COLORS.cyan} fontWeight="bold" mb={1}>KEY LESSON</Text>
                  <Text fontSize="sm" color="gray.200">{step.keyLesson}</Text>
                </Box>
                <PokerButton onClick={next} style={{ alignSelf: 'center' }}>
                  {stepIdx + 1 >= challenge.steps.length ? 'See Results' : 'Next Decision'}
                </PokerButton>
              </>
            )}
          </VStack>
        </PokerCard>
      </Container>
    </Box>
  );
};

export default TournamentChallengePage;
